import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Row,
  Spinner,
  Table,
} from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faExclamationTriangle,
  faShieldAlt,
  faSync,
} from "@fortawesome/free-solid-svg-icons";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import BackButton from "../../components/BackButton";
import AuditStatusBadge from "../../components/audit/AuditStatusBadge";
import {
  canActAtStage,
  isAuditorGeneralAccount,
  isReleaseAuditorAccount,
} from "../../components/audit/auditRole";
import AuditExceptionModal from "./AuditExceptionModal";
import AuditTimeline from "./AuditTimeline";
import { fetchApi, postApi } from "../../redux/api";
import { formatNumber } from "../../redux/utils";
import { Routes } from "../../routes";
import { confirmDialog } from "../../components/ConfirmDialog";

const ACTIONABLE = {
  1: ["Not Audited", "Pending AG Review"],
  2: ["AG Approved"],
};

const STAGE_TITLE = {
  1: "Auditor General review",
  2: "Release audit",
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const Field = ({ label, value, children }) => (
  <Col xs={12} sm={6} lg={3} className="mb-3">
    <div className="text-muted small">{label}</div>
    <div className="fw-bold">{children || value || "—"}</div>
  </Col>
);

/**
 * The stage-1/2 review screen for one release.
 *
 * Every action here writes only the audit columns on `approval_collection`
 * (`audit_status`, `audit_stage`, `audit_by`, `audit_at`) plus a row in
 * `auditor_general_remark`. The release status the treasury pipeline depends on
 * is never touched, so a flag cannot stall a payment.
 */
const AuditReleaseView = () => {
  const [searchParams] = useSearchParams();
  const approvalNo = searchParams.get("approval_no") || "";
  const stage = Number(searchParams.get("stage")) === 2 ? 2 : 1;
  const user = useSelector((state) => state.auth.user);
  const roleCanAct = canActAtStage(user, stage);

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [flagOpen, setFlagOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadDetail = useCallback(() => {
    if (!approvalNo) {
      setError("No release selected. Open one from the audit queue.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    fetchApi(`audit/release/${encodeURIComponent(approvalNo)}`)
      .then((response) => {
        if (response?.success) {
          setDetail(response);
        } else {
          setDetail(null);
          setError(response?.message || "Could not load this release.");
        }
      })
      .catch(() => {
        setDetail(null);
        setError("Could not load this release. Please try again.");
      })
      .finally(() => setLoading(false));
  }, [approvalNo]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const release = detail?.release || null;
  const items = detail?.items || [];
  const timeline = detail?.timeline || [];
  const categories = detail?.categories || [];

  const canAct = useMemo(() => {
    if (!release || !roleCanAct) return false;
    return (ACTIONABLE[stage] || []).includes(release.audit_status);
  }, [release, stage, roleCanAct]);

  const backTo = stage === 2 ? Routes.audit.releaseDashboard : Routes.audit.agDashboard;

  const approve = async () => {
    const stageLabel = stage === 1 ? "the Auditor General" : "the release auditor";
    const outcome = stage === 1 ? "AG Approved" : "Audit Approved";
    const confirmed = await confirmDialog(
      `Approve ${approvalNo} on behalf of ${stageLabel}? The audit status becomes ${outcome} — the release status stays exactly as it is.`,
      { title: stage === 1 ? "Approve release" : "Record Audit Approved", confirmText: "Approve" }
    );
    if (!confirmed) return;

    setSubmitting(true);
    try {
      const response = await postApi("audit/approve", { approval_no: approvalNo, stage });
      if (response?.success) {
        toast.success(stage === 1 ? "AG Approved recorded" : "Audit Approved recorded");
        loadDetail();
      } else {
        toast.error(response?.message || "Could not record the approval");
      }
    } catch (err) {
      toast.error("Could not record the approval");
    } finally {
      setSubmitting(false);
    }
  };

  const recordException = async ({ category, remark }) => {
    setSubmitting(true);
    try {
      const response = await postApi("audit/flag", {
        approval_no: approvalNo,
        stage,
        category,
        remark,
      });
      if (response?.success) {
        toast.success(
          stage === 1
            ? "AG Flagged — exception recorded"
            : "Audit Flagged — exception recorded"
        );
        setFlagOpen(false);
        loadDetail();
      } else {
        toast.error(response?.message || "Could not record the exception");
      }
    } catch (err) {
      toast.error("Could not record the exception");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !detail) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 mb-0 text-muted">Loading release…</p>
      </div>
    );
  }

  if (error) {
    return (
      <>
        <BackButton route={backTo} />
        <div className="alert alert-danger mt-3">{error}</div>
      </>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2">
        <div className="d-block mb-3 mb-md-0">
          <BackButton route={backTo} />
          <h4 className="mb-0 mt-2">
            <FontAwesomeIcon icon={faShieldAlt} className="me-2 text-primary" />
            {STAGE_TITLE[stage]}
          </h4>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={loadDetail}
            disabled={loading || submitting}
          >
            <FontAwesomeIcon icon={faSync} className={`me-1 ${loading ? "fa-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            variant="success"
            size="sm"
            onClick={approve}
            disabled={!canAct || submitting}
          >
            <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
            Approve
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => setFlagOpen(true)}
            disabled={!canAct || submitting}
          >
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
            Flag
          </Button>
        </div>
      </div>

      <Card border="light" className="shadow-sm mb-4">
        <Card.Body>
          <div className="d-flex flex-wrap justify-content-between align-items-start mb-2">
            <div>
              <h5 className="mb-1">{release.approval_no}</h5>
              <p className="text-muted mb-0">{release.mda_name}</p>
            </div>
            <div className="text-md-end mt-2 mt-md-0">
              <AuditStatusBadge status={release.audit_status} />
              <div className="small text-muted mt-2">
                Release status: <strong>{release.approval_status || "—"}</strong>
              </div>
            </div>
          </div>

          <Row className="mt-3">
            <Field label="Release type" value={release.approval_type} />
            <Field label="Collection date" value={formatDate(release.collection_date)} />
            <Field label="Approval date" value={formatDate(release.approval_date)} />
            <Field label="Approved by" value={release.approved_by} />
            <Field label="MDA code" value={release.mda_code} />
            <Field label="Total" value={formatNumber(detail?.total_amount)} />
            <Field label="Audited at">{formatDate(release.audit_at)}</Field>
            <Field label="Audit decision by">
              {release.auditor_name || (release.audit_by ? `User #${release.audit_by}` : "—")}
            </Field>
          </Row>

          {release.narration && (
            <div className="mt-2">
              <div className="text-muted small">Narration</div>
              <div style={{ whiteSpace: "pre-wrap" }}>{release.narration}</div>
            </div>
          )}

          {!canAct && (
            <div className="alert alert-info mt-3 mb-0 py-2">
              {!roleCanAct
                ? stage === 2
                  ? isAuditorGeneralAccount(user)
                    ? `The Auditor General can read this second review but cannot action it. The Audit Approved / Audit Flagged outcome is recorded by a release auditor.`
                    : "Only a release auditor can record the second review for this release."
                  : isReleaseAuditorAccount(user)
                  ? "Only the Auditor General can record the first review. The release auditor takes over once it has been approved by the AG."
                  : "Only the Auditor General can record the first review for this release."
                : stage === 2
                ? ["Audit Approved", "Audit Flagged"].includes(release.audit_status)
                  ? `The second review is complete — this release is ${release.audit_status} and no longer in the queue.`
                  : "This release is not in the stage 2 queue (it must be approved by the Auditor General first)."
                : ["AG Approved", "AG Flagged", "Audit Approved", "Audit Flagged"].includes(
                    release.audit_status
                  )
                ? `The first review is already recorded — this release is ${release.audit_status}.`
                : "This release is not in the stage 1 queue (it must be at Budget Approval Released and not yet audited)."}
            </div>
          )}
        </Card.Body>
      </Card>

      <Card border="light" className="shadow-sm mb-4">
        <Card.Header className="border-0 d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Release lines</h5>
          <small className="text-muted">{items.length} line(s)</small>
        </Card.Header>
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0 align-items-center">
            <thead>
              <tr>
                {["Economic code", "Description", "Program", "Amount"].map((heading, index) => (
                  <th
                    key={heading}
                    className={index === 3 ? "text-end" : ""}
                    style={{ backgroundColor: "#099019", color: "#fff", fontWeight: 600 }}
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{item.economic_code}</td>
                  <td>{item.description_code}</td>
                  <td>{item.program_code || "—"}</td>
                  <td className="text-end">{formatNumber(item.amount)}</td>
                </tr>
              ))}
              {!items.length && (
                <tr>
                  <td colSpan={4} className="text-center text-muted py-3">
                    No lines recorded for this release.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card border="light" className="shadow-sm mb-4">
        <Card.Header className="border-0 d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Exceptions</h5>
          <Badge bg={detail?.exceptions?.length ? "danger" : "light"} text={detail?.exceptions?.length ? "light" : "dark"}>
            {detail?.exceptions?.length || 0}
          </Badge>
        </Card.Header>
        <Card.Body>
          {detail?.exceptions?.length ? (
            <ul className="list-unstyled mb-0">
              {detail.exceptions.map((exception) => (
                <li key={exception.id} className="mb-3">
                  <div className="fw-bold">
                    <Badge bg="danger" className="me-2">
                      {exception.category}
                    </Badge>
                    <small className="text-muted">
                      {exception.commented_by || "Auditor"} · {formatDate(exception.created_at)}
                    </small>
                  </div>
                  <div style={{ whiteSpace: "pre-wrap" }}>{exception.remark}</div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted mb-0">No exceptions raised on this release.</p>
          )}
        </Card.Body>
      </Card>

      <Card border="light" className="shadow-sm mb-4">
        <Card.Header className="border-0">
          <h5 className="mb-0">Timeline</h5>
        </Card.Header>
        <Card.Body>
          <AuditTimeline entries={timeline} />
        </Card.Body>
      </Card>

      <AuditExceptionModal
        show={flagOpen}
        onHide={() => setFlagOpen(false)}
        onSubmit={recordException}
        categories={categories}
        submitting={submitting}
        releaseLabel={approvalNo}
      />
    </>
  );
};

export default AuditReleaseView;
